import { GalleryIcon } from '../icons/gallery-icon';
import { ChatIcon } from '../icons/chat-icon';
import {
  Tabs,
  Tab,
  Tooltip,
  Dropdown,
  DropdownItem,
  DropdownTrigger,
  Button,
  DropdownMenu,
  cn,
} from '@nextui-org/react';
import { useRouter } from 'next/router';
import { useAIModels } from '@/data/aiModels';
import { useCategories } from '@/data/category';
import { useTagsQuery } from '@/data/tag';
import { getTypeId } from '@/data/utils/utils';
import { useProductLoader } from '@/components/product/product-loader';

function updateQueryWithFilteredSlugs(
  queryKey: any,
  data: any,
  query: any,
  type_id: any
) {
  let keyValue = query[queryKey];
  // Splitting the keyValue by commas to handle it as an array consistently
  keyValue = keyValue ? keyValue.split(',') : null;

  if (keyValue && keyValue.length) {
    const slugSet = new Set(
      data.filter((item) => item.type_id === type_id).map((item) => item.slug)
    );

    // Generate filteredSlugs by maintaining the order of slugs in keyValue
    const filteredSlugs = keyValue
      .filter((slug) => slugSet.has(slug)) // Keep only slugs that are in the data array and match the type_id
      .join(',');

    if (filteredSlugs.length > 0) {
      query[queryKey] = filteredSlugs;
    } else {
      delete query[queryKey];
    }
  }
}

const generateSlugPath = (item: { slug: string; parent?: any }): string => {
  const slug = item.slug;
  if (item.parent) {
    return `${generateSlugPath(item.parent)}\\${slug}`;
  }
  return slug;
};

export default function ProductTypeTabs({
  small = false,
  className = '',
}: any) {
  const router = useRouter();
  const { aiModels } = useAIModels();
  const { categories } = useCategories();
  const { tags } = useTagsQuery({ language: router.locale });
  const { setIsLoading } = useProductLoader();
  function handleChange(value: any) {
    // const { orderBy, sortedBy } = values;
    setIsLoading(true);
    const query = router.query;
    if (value !== 'all') {
      query.types = value;

      // Clear all irrelevant filters
      const type_id = getTypeId(value);
      updateQueryWithFilteredSlugs('aiModels', aiModels, query, type_id);
      // TODO: improve function to handle categories and tags (without parents)?
      updateQueryWithFilteredSlugs(
        'categories',
        categories.map((category) => ({
          ...category,
          slug: generateSlugPath(category),
        })),
        query,
        type_id
      );
      updateQueryWithFilteredSlugs('tags', tags, query, type_id);
    } else {
      delete query.types;
    }

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { scroll: false }
    );
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <GalleryIcon />;
      case 'text':
        return <ChatIcon />;
      default:
        return <GalleryIcon />;
    }
  };

  return (
    <div className="flex flex-col">
      {small ? (
        <Dropdown placement="top-start">
          <DropdownTrigger>
            <Button
              variant="solid"
              size="lg"
              isIconOnly
              className={cn('bg-default-400/20 capitalize', className)}
              disableRipple
              endContent={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-3 w-3 ml-1"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                  />
                </svg>
              }
            >
              {(router.query.types as string)
                ? getTypeIcon(router.query.types as string)
                : 'All'}
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            selectedKeys={
              (router.query.types as string)
                ? [router.query.types as string]
                : ['all']
            }
            disallowEmptySelection
            selectionMode="single"
          >
            <DropdownItem onClick={() => handleChange('all')} key="all">
              All Types
            </DropdownItem>
            <DropdownItem onClick={() => handleChange('image')} key="image">
              Image
            </DropdownItem>
            <DropdownItem onClick={() => handleChange('text')} key="text">
              Text
            </DropdownItem>
            {/* <DropdownItem  onClick={() => handleChange('video')} key="video">
              Videos
            </DropdownItem> */}
          </DropdownMenu>
        </Dropdown>
      ) : (
        <Tabs
          aria-label="Options"
          variant={small ? 'solid' : 'underlined'}
          color="primary"
          radius="full"
          size="lg"
          className="justify-start"
          selectedKey={(router.query.types as string) ?? 'all'}
          onSelectionChange={(e) => handleChange(e)}
        >
          <Tab
            key="all"
            title={
              <div className="flex items-center space-x-2">
                {/* {selected === 'all' && <span>All</span>} */}

                {small ? <span>All</span> : <span>All Types</span>}
              </div>
            }
          />
          <Tab
            key="image"
            title={
              <div className="flex items-center space-x-2">
                <GalleryIcon />
                {/* {selected === 'images' && <span>Images</span>} */}
                {small ? null : <span className="hidden md:block">Image</span>}
              </div>
            }
          />
          <Tab
            key="text"
            title={
              <div className="flex items-center space-x-2">
                <ChatIcon />
                {/* {selected === 'text' && <span>Text</span>} */}
                {small ? null : <span className="hidden md:block">Text</span>}
              </div>
            }
          />
          <Tab
            key="video"            
            title={
                <div className="flex items-center space-x-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
                    />
                  </svg>

                  {/* {selected === 'text' && <span>Text</span>} */}

                  {small ? null : (
                    <span className="hidden md:block">Video</span>
                  )}
                </div>
            }
          />
          {/* <Tab
          key="guide"
          title={
            <div className="flex items-center space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>

              <span className="hidden md:block">Guides</span>
            </div>
          }
        /> */}
          {/* <Tab
          key="code"
          title={
            <div className="flex items-center space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
                />
              </svg>

              <span>Code</span>
            </div>
          }
        /> */}
        </Tabs>
      )}
    </div>
  );
}
